import { service } from '../../../../redux/api/axios';
import type {
  AdminResetRequest,
  CohortSummary,
  CohortWarningDraft,
  CreateCohortRequest,
  ListAuditEventsQuery,
  ListAuditEventsResponse,
  ListCohortMembersResponse,
  SendWarningResponse,
  UpdateCohortRequest,
} from '../types/mfaAdmin.types';

// One place for every admin-side MFA call. All routes ride the standard
// service axios instance so the JWT interceptor + baseURL logic apply as
// they do to the rest of the admin panel.

export const adminResetMfa = (targetUserId: string, body: AdminResetRequest) =>
  service
    .post<{ message: string }>(`/mfa/admin/reset/${targetUserId}`, body)
    .then((r) => r.data);

export const listAuditEvents = (query: ListAuditEventsQuery = {}) =>
  service
    .get<ListAuditEventsResponse>('/mfa/admin/audit', { params: query })
    .then((r) => r.data);

// ── Cohorts ────────────────────────────────────────────────────────────────

export const listCohorts = () =>
  service
    .get<{ cohorts: CohortSummary[] }>('/mfa/admin/cohorts')
    .then((r) => r.data.cohorts);

export const createCohort = (body: CreateCohortRequest) =>
  service.post<CohortSummary>('/mfa/admin/cohorts', body).then((r) => r.data);

export const updateCohortSwitchOnDate = (
  name: string,
  body: UpdateCohortRequest
) =>
  service
    .patch<CohortSummary>(`/mfa/admin/cohorts/${name}`, body)
    .then((r) => r.data);

export const deleteCohort = (name: string) =>
  service
    .delete<{ message: string }>(`/mfa/admin/cohorts/${name}`)
    .then((r) => r.data);

export const listCohortMembers = (name: string) =>
  service
    .get<ListCohortMembersResponse>(`/mfa/admin/cohorts/${name}/members`)
    .then((r) => r.data);

export const assignUsersToCohort = (name: string, userIds: string[]) =>
  service
    .post<{ modified: number }>(`/mfa/admin/cohorts/${name}/assign`, {
      userIds,
    })
    .then((r) => r.data);

// Assign every user of one branch (gemeente) in a single call. Returns the
// number actually moved — users already in this cohort are not recounted.
export const assignBranchToCohort = (name: string, branch: string) =>
  service
    .post<{ modified: number }>(`/mfa/admin/cohorts/${name}/assign-branch`, {
      branch,
    })
    .then((r) => r.data);

export const unassignUserFromCohort = (userId: string) =>
  service
    .delete<{ modified: number }>(`/mfa/admin/cohorts/members/${userId}`)
    .then((r) => r.data);

// Editable draft: default copy with the switch-on date already substituted,
// plus how many people would actually receive it.
export const getCohortWarningDraft = (name: string) =>
  service
    .get<CohortWarningDraft>(`/mfa/admin/cohorts/${name}/warning-draft`)
    .then((r) => r.data);

export const sendCohortWarningEmails = (
  name: string,
  body: { subject: string; message: string }
) =>
  service
    .post<SendWarningResponse>(`/mfa/admin/cohorts/${name}/send-warning`, body)
    .then((r) => r.data);
